import React, { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { supabase } from '../../lib/supabase'
import { useAuth } from '../../contexts/AuthContext'
import LoadingSpinner from '../../components/ui/LoadingSpinner'
import TopNavigation from '../../../components/TopNavigation'
import { CheckCircle, XCircle } from 'lucide-react'
import { verifyEmailCode, resendVerificationCode } from '../../lib/auth-helpers'

const AuthCallbackPage: React.FC = () => {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const { refreshUser, user, secretUser } = useAuth()
  const [status, setStatus] = useState<'code_input' | 'success' | 'error'>('code_input')
  const [message, setMessage] = useState('Please enter the 6-digit code from your email to complete verification.')
  const [resendEmail, setResendEmail] = useState<string>('')
  const [resending, setResending] = useState<boolean>(false)
  const [resendFeedback, setResendFeedback] = useState<string>('')
  const [codeInput, setCodeInput] = useState<string>('')
  const [verifying, setVerifying] = useState<boolean>(false)
  const [verifyFeedback, setVerifyFeedback] = useState<string>('')

  useEffect(() => {
    const handleAuthCallback = async () => {
      try {
        // Parse both query params and hash parameters (implicit flow places tokens/errors in hash)
        const hashStr = window.location.hash.startsWith('#')
          ? window.location.hash.substring(1)
          : window.location.hash
        const hashParams = new URLSearchParams(hashStr)

        // Prefer query params; fall back to hash params when missing
        const qp = (k: string) => searchParams.get(k)
        const hp = (k: string) => hashParams.get(k)

        const accessToken = qp('access_token') || hp('access_token')
        const refreshToken = qp('refresh_token') || hp('refresh_token')
        const tokenHash = qp('token_hash') || hp('token_hash')
        const rawType = (qp('type') || hp('type') || 'email').toLowerCase()
        const normalizedType = rawType === 'signup' || rawType === 'magiclink' ? 'email' : rawType

        // First, try to verify via token_hash (magic link flow)
        if (tokenHash) {
          try {
            const { error } = await supabase.auth.verifyOtp({
              token_hash: tokenHash,
              type: normalizedType as 'email' | 'recovery' | 'invite' | 'email_change'
            })
            if (!error) {
              await refreshUser()
              setStatus('success')
              setMessage('Email verified successfully! Redirecting...')
              setTimeout(() => navigate('/login'), 1500)
              return
            }
          } catch (e) {
            console.error('Token hash verification failed:', e)
          }
        }

        // Next, handle successful implicit tokens - everything else goes to code input
        if (accessToken || refreshToken) {
          try {
            const { data: { session } } = await supabase.auth.getSession()
            if (session) {
              setStatus('success')
              setMessage('Email verified and signed in successfully!')
              setTimeout(() => {
                navigate('/dashboard')
              }, 1500)
              return
            }
          } catch (e) {
            console.error('Session verification failed:', e)
          }
        }

        // For all other cases (errors, tokens, codes), default to code input
        setStatus('code_input')
        setMessage('Please enter the 6-digit verification code from your email.')
        
      } catch (error) {
        console.error('Auth callback error:', error)
        setStatus('code_input')
        setMessage('Please enter the 6-digit verification code from your email.')
      }
    }

    handleAuthCallback()
  }, [searchParams, navigate, refreshUser])

  useEffect(() => {
    // Pre-fill email from authenticated context if available
    const urlEmail = new URLSearchParams(window.location.search).get('email')
    if (!resendEmail) {
      if (user?.email) setResendEmail(user.email)
      else if (urlEmail) setResendEmail(urlEmail)
    }
  }, [user, resendEmail])

  const handleResend = async () => {
    try {
      setResendFeedback('')
      setResending(true)
      if (!resendEmail) {
        setResendFeedback('Please enter your email address.')
        setResending(false)
        return
      }
      
      
      // Get user metadata for email personalization
      const userData = user?.user_metadata || {}
      const fullName = userData.full_name || 
        (secretUser?.first_name && secretUser?.last_name 
          ? `${secretUser.first_name} ${secretUser.last_name}`.trim()
          : secretUser?.first_name || '')

      await resendVerificationCode(resendEmail, {
        full_name: fullName,
        fullName: fullName, // camelCase version
        first_name: userData.first_name || secretUser?.first_name || '',
        last_name: userData.last_name || secretUser?.last_name || ''
      })
      setResendFeedback('New verification code sent to your email. Enter the 6-digit code above.')
    } catch (err: any) {
      setResendFeedback(err?.message || 'Failed to send verification code.')
    } finally {
      setResending(false)
    }
  }

  const handleVerifyCode = async () => {
    try {
      setVerifyFeedback('')
      setVerifying(true)
      const candidateEmail = user?.email || resendEmail
      if (!candidateEmail) {
        setVerifyFeedback('Enter your email above so we can verify your code.')
        setVerifying(false)
        return
      }
      if (!codeInput) {
        setVerifyFeedback('Enter the 6-digit code from your email.')
        setVerifying(false)
        return
      }
      await verifyEmailCode(candidateEmail, codeInput.trim())
      await refreshUser()
      setStatus('success')
      setMessage('Email verified successfully! Redirecting...')
      setTimeout(() => navigate('/login'), 1500)
    } catch (err: any) {
      setVerifyFeedback(err?.message || 'Invalid or expired code. Request a new one and try again.')
    } finally {
      setVerifying(false)
    }
  }

  return (
    <div className="min-h-screen-safe bg-background flex flex-col">
      <TopNavigation />
      
      <div className="flex-1 flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-md text-center">
          {status === 'success' && (
            <div className="space-y-4">
              <CheckCircle size={64} className="mx-auto text-green-500" />
              <h2 className="text-xl font-semibold text-foreground">
                Verification Successful!
              </h2>
              <p className="text-muted-foreground">
                {message}
              </p>
              <p className="text-sm text-muted-foreground">
                Redirecting you to login...
              </p>
            </div>
          )}

          {(status === 'code_input' || status === 'error') && (
            <div className="space-y-4">
              <div className="mx-auto w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center">
                <span className="text-2xl">📧</span>
              </div>
              <h2 className="text-xl font-semibold text-foreground">
                Email Verification
              </h2>
              <p className="text-muted-foreground">
                {message}
              </p>
              
              {/* Code input - primary verification method */}
              <div className="space-y-2 text-left">
                <label className="block text-sm font-medium text-foreground">Verification Code</label>
                <input
                  type="text"
                  inputMode="numeric"
                  placeholder="000000"
                  maxLength={6}
                  value={codeInput}
                  onChange={(e) => setCodeInput(e.target.value.replace(/\D/g, ''))}
                  className="w-full px-3 py-2 bg-transparent border border-border text-foreground rounded-md focus:outline-none focus:ring-2 focus:ring-ring text-center text-lg font-mono"
                  autoFocus
                />
                <button
                  onClick={handleVerifyCode}
                  disabled={verifying || codeInput.length !== 6}
                  className="bg-primary text-primary-foreground hover:bg-primary/90 w-full px-4 py-2 text-sm font-medium rounded-md transition-colors disabled:opacity-60"
                >
                  {verifying ? 'Verifying…' : 'Verify Email'}
                </button>
                {verifyFeedback && (
                  <p className="text-xs mt-1 text-red-500">{verifyFeedback}</p>
                )}
              </div>

              {/* Email input and resend */}
              <div className="space-y-2 text-left">
                <label className="block text-sm font-medium text-foreground">Your Email</label>
                <input
                  type="email"
                  value={resendEmail}
                  onChange={(e) => setResendEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="w-full px-3 py-2 bg-transparent border border-border text-foreground rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
                />
                <button
                  onClick={handleResend}
                  disabled={resending || !resendEmail}
                  className="border border-border text-foreground hover:bg-muted w-full px-4 py-2 text-sm font-medium rounded-md transition-colors disabled:opacity-60"
                >
                  {resending ? 'Sending…' : 'Send New Code'}
                </button>
                {resendFeedback && (
                  <p className="text-xs mt-1 text-muted-foreground">{resendFeedback}</p>
                )}
              </div>
              
              {/* Navigation options */}
              <div className="pt-4 space-y-2">
                <button
                  onClick={() => navigate('/login')}
                  className="text-sm text-muted-foreground hover:text-foreground underline"
                >
                  Back to Sign In
                </button>
                <br />
                <button
                  onClick={() => navigate('/register')}
                  className="text-sm text-muted-foreground hover:text-foreground underline"
                >
                  Create New Account
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default AuthCallbackPage